"use strict";
/**
 * useOrgLookupData - Loads and caches reference data for an organization
 *
 * Bulk-loads terms, courses, academic programs, and cohorts in parallel.
 * Returns Maps keyed by entity ID for O(1) lookups during entity resolution.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOrgLookupData = useOrgLookupData;
const react_1 = require("react");
const termApi_1 = require("../helpers/termApi");
const courseApi_1 = require("../helpers/courseApi");
const academicProgramApi_1 = require("../helpers/academicProgramApi");
const cohortApi_1 = require("../helpers/cohortApi");
const EMPTY_MAP = new Map();
function useOrgLookupData(orgId) {
    const [termsMap, setTermsMap] = (0, react_1.useState)(EMPTY_MAP);
    const [coursesMap, setCoursesMap] = (0, react_1.useState)(EMPTY_MAP);
    const [programsMap, setProgramsMap] = (0, react_1.useState)(EMPTY_MAP);
    const [cohortsMap, setCohortsMap] = (0, react_1.useState)(EMPTY_MAP);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        if (!orgId) {
            setTermsMap(EMPTY_MAP);
            setCoursesMap(EMPTY_MAP);
            setProgramsMap(EMPTY_MAP);
            setCohortsMap(EMPTY_MAP);
            return;
        }
        let cancelled = false;
        const load = () => __awaiter(this, void 0, void 0, function* () {
            setLoading(true);
            setError(null);
            // Load each resource independently so one failure doesn't block the others.
            // Angular loads these via separate NgRx effects that don't depend on each other.
            const results = yield Promise.allSettled([
                termApi_1.termApi.getTermsByOrganization(orgId),
                courseApi_1.courseApi.getCoursesByOrganization(orgId),
                academicProgramApi_1.academicProgramApi.getProgramsByOrganization(orgId),
                cohortApi_1.cohortApi.getCohortsByOrg(orgId),
            ]);
            if (cancelled)
                return;
            const [termsResult, coursesResult, programsResult, cohortsResult] = results;
            if (termsResult.status === 'fulfilled') {
                setTermsMap(new Map(termsResult.value.map(t => [t.id, t])));
            }
            if (coursesResult.status === 'fulfilled') {
                setCoursesMap(new Map(coursesResult.value.map(c => [c.id, c])));
            }
            if (programsResult.status === 'fulfilled') {
                setProgramsMap(new Map(programsResult.value.map(p => [p.id, p])));
            }
            if (cohortsResult.status === 'fulfilled') {
                setCohortsMap(new Map(cohortsResult.value.map(c => [c.id, c])));
            }
            // Report first error if any
            const firstError = results.find(r => r.status === 'rejected');
            if (firstError && !cancelled) {
                const err = firstError.reason;
                setError(err instanceof Error ? err.message : 'Failed to load some lookup data');
            }
            if (!cancelled) {
                setLoading(false);
            }
        });
        load();
        return () => { cancelled = true; };
    }, [orgId]);
    return { termsMap, coursesMap, programsMap, cohortsMap, loading, error };
}
